import { supabase } from '@/lib/supabase/client'

export type NDTReportMethod = 'MT' | 'VT' | 'PT' | 'LT' | 'ET' | 'UT' | 'RT' | 'CTH' | 'RSC' | 'SR' | 'TR'

/** Methods that previously stored files under the bare order folder (no method prefix). */
const LEGACY_BARE_FOLDER_METHODS: ReadonlySet<NDTReportMethod> = new Set(['VT', 'PT', 'LT'])

/**
 * Method-scoped storage folder for an order number.
 * Sequences are isolated per NDT type (e.g. MT-2606001 vs VT-2606001).
 */
export function getReportStorageFolder(
  method: NDTReportMethod,
  reportNumber: string,
): string {
  const baseOrderNumber = normalizeBaseOrderNumber(method, reportNumber)
  if (!baseOrderNumber) return ''
  return `${method}-${baseOrderNumber}`
}

function normalizeBaseOrderNumber(method: NDTReportMethod, reportNumber: string): string {
  return reportNumber
    .trim()
    .replace(new RegExp(`^${method}-`, 'i'), '')
}

/** Extract trailing 1–3 digit sequence from order numbers / filenames. */
function parseSequenceToken(value: string): number | null {
  const match = value.match(/(?:[._-])(\d{1,3})(?:\.pdf)?$/i)
  if (!match) return null
  const n = Number.parseInt(match[1], 10)
  return Number.isFinite(n) && n > 0 ? n : null
}

async function maxSequenceFromStorageFolder(folder: string): Promise<number> {
  if (!folder) return 0

  const { data: existingFiles, error } = await supabase.storage
    .from('reports')
    .list(folder, { limit: 1000 })

  if (error && error.message !== 'Bucket not found') {
    throw error
  }

  if (!existingFiles?.length) return 0

  return existingFiles.reduce((max, file) => {
    const seq = parseSequenceToken(file.name)
    return seq && seq > max ? seq : max
  }, 0)
}

async function maxSequenceFromDatabase(
  method: NDTReportMethod,
  baseOrderNumber: string,
): Promise<number> {
  if (!baseOrderNumber) return 0

  const prefix = `${method}-${baseOrderNumber}.`

  const { data, error } = await supabase
    .from('ndt_reports')
    .select('order_number')
    .eq('test_type', method)
    .ilike('order_number', `${prefix}%`)

  if (error) {
    console.warn(`Could not read existing ${method} sequences from ndt_reports:`, error.message)
    return 0
  }

  if (!data?.length) return 0

  return data.reduce((max, row) => {
    const seq = parseSequenceToken(row.order_number || '')
    return seq && seq > max ? seq : max
  }, 0)
}

export async function getNextReportSequence(
  storageFolder: string,
  options?: {
    method?: NDTReportMethod
    baseOrderNumber?: string
  },
): Promise<string> {
  const folder = storageFolder.trim()
  const method = options?.method
  const baseOrderNumber = options?.baseOrderNumber?.trim() || ''

  const candidates: Promise<number>[] = [maxSequenceFromStorageFolder(folder)]

  if (method && baseOrderNumber && LEGACY_BARE_FOLDER_METHODS.has(method)) {
    candidates.push(maxSequenceFromStorageFolder(baseOrderNumber))
  }

  if (method && baseOrderNumber) {
    candidates.push(maxSequenceFromDatabase(method, baseOrderNumber))
  }

  const maxima = await Promise.all(candidates)
  const next = Math.max(0, ...maxima) + 1
  return String(next).padStart(3, '0')
}

export async function getNextMethodReportSequence(
  method: NDTReportMethod,
  reportNumber: string,
): Promise<{ storageFolder: string; sequenceNumber: string; reportId: string }> {
  const baseOrderNumber = normalizeBaseOrderNumber(method, reportNumber)
  const storageFolder = getReportStorageFolder(method, reportNumber)
  const sequenceNumber = await getNextReportSequence(storageFolder, {
    method,
    baseOrderNumber,
  })
  const reportId = storageFolder ? `${storageFolder}.${sequenceNumber}` : `---.${sequenceNumber}`
  return { storageFolder, sequenceNumber, reportId }
}

export function generateOrderNumber(reportCode: string): string {
  if (!reportCode) return ''
  const now = new Date()
  const year = now.getFullYear().toString().slice(-2)
  const month = (now.getMonth() + 1).toString().padStart(2, '0')
  return `I${year}${month}${reportCode.padStart(3, '0')}`
}
